import React, { FunctionComponent, useRef, useState, useEffect } from "react";
import { Button, Classes, Dialog, Intent, FileInput, Callout, ProgressBar, Tabs, Tab } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Flex } from "@rebass/grid";
import apiService from "src/services/api";

interface Props {
  isOpen: boolean;
  onClose(): void;
  instanceId?: number;
}

const AgenticAIModalDialog: FunctionComponent<Props> = ({ isOpen, onClose, instanceId }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("download");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("download");
      setSelectedFile(null);
      setUploadStatus("");
      setUploading(false);
      setDownloading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isOpen]);

  const handleFileChange = (e: React.FormEvent<HTMLInputElement>) => {
    const files = (e.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const file = files[0];
      const fileName = file.name.toLowerCase();
      
      // Validate file extension
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        setUploadStatus("Invalid file type. Please select an Excel file (.xlsx or .xls).");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      
      setSelectedFile(file);
      setUploadStatus("");
    }
  };

  const handleDownload = async () => {
    if (!instanceId) {
      setUploadStatus("Instance ID is required for download.");
      return;
    }
    
    setDownloading(true);
    setUploadStatus("");
    
    try {
      // Get the response with the blob URL
      const response = await apiService.get(`/instances/${instanceId}/agentic_metadata`);
      
      if (!response.AgenticMetadataBlobURL) {
        throw new Error("No download URL received from server");
      }
      
      // Fetch the actual file from the blob URL
      const fileResponse = await fetch(response.AgenticMetadataBlobURL);
      if (!fileResponse.ok) {
        throw new Error("Failed to download file from blob storage");
      }
      
      const blob = await fileResponse.blob();
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `agentic_metadata_${instanceId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setUploadStatus("Download successful!");
    } catch (e: any) {
      // Extract the actual error message from the server response
      let errorMessage = "Please try again.";
      if (e?.body?.message) {
        errorMessage = e.body.message;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      setUploadStatus(`Download failed: ${errorMessage}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !instanceId) {
      setUploadStatus("File and Instance ID are required for upload.");
      return;
    }
    
    setUploading(true);
    setUploadStatus("");
    setUploadProgress(0);
    
    try {
      // Step 1: POST to /uploads to get BlobURL
      setUploadProgress(0.1);
      const uploadResponse = await apiService.post<{ BlobURL: string; id: number }>(
        "/uploads",
        {
          FileName: selectedFile.name,
          Comment: `Agentic Metadata Upload for Model Instance - ${instanceId}`,
        }
      );
      
      const { BlobURL, id } = uploadResponse;
      
      // Step 2: PUT file to BlobURL
      setUploadProgress(0.3);
      await apiService.uploadToBlob(BlobURL, selectedFile);
      
      // Step 3: POST to /uploads/agentic_metadata to validate
      setUploadProgress(0.7);
      await apiService.post("/uploads/agentic_metadata", {
        ModelInstanceID: instanceId,
        UploadID: id,
      });
      
      setUploadProgress(1);
      setUploadStatus("Upload successful!");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (e: any) {
      // Extract the actual error message from the server response
      let errorMessage = "Please try again.";
      if (e?.body?.message) {
        errorMessage = e.body.message;
      } else if (e?.message) {
        errorMessage = e.message;
      }
      setUploadStatus(`Upload failed: ${errorMessage}`);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  return (
    <Dialog
      title="Metadata Management"
      isOpen={isOpen}
      onClose={onClose}
      icon={IconNames.EXCHANGE}
    >
      <div className={Classes.DIALOG_BODY}>
        <style>
          {`
            .bp4-tab:focus {
              outline: none !important;
            }
            .bp4-tab[aria-selected="true"] {
              box-shadow: none !important;
            }
            .bp4-callout.bp4-callout-icon > .bp4-icon:first-child {
              top: 18px !important;
            }
          `}
        </style>
        <Tabs
          id="metadata-tabs"
          selectedTabId={activeTab}
          onChange={(newTabId) => {
            setActiveTab(newTabId as string);
            setUploadStatus("");
          }}
        >
          <Tab
            id="download"
            title="Download"
            panel={
              <div style={{ paddingTop: 16 }}>
                <Callout intent={Intent.PRIMARY} icon={IconNames.INFO_SIGN} style={{ marginBottom: 16, display: "flex", alignItems: "flex-start" }}>
                  <div>
                    Download the metadata Excel file to view and edit the agentic metadata.
                  </div>
                </Callout>
                <Button
                  icon={IconNames.DOWNLOAD}
                  text="Download Metadata"
                  onClick={handleDownload}
                  disabled={!instanceId || downloading}
                  loading={downloading}
                  intent={Intent.PRIMARY}
                  style={{ marginBottom: 16 }}
                />
                {uploadStatus && activeTab === "download" && (
                  <Callout intent={uploadStatus.includes("success") ? Intent.SUCCESS : Intent.DANGER}>
                    {uploadStatus}
                  </Callout>
                )}
              </div>
            }
          />
          <Tab
            id="upload"
            title="Upload"
            panel={
              <div style={{ paddingTop: 16 }}>
                <Callout intent={Intent.PRIMARY} icon={IconNames.INFO_SIGN} style={{ marginBottom: 16, display: "flex", alignItems: "flex-start" }}>
                  <div>
                    Upload the edited metadata Excel file.<br />
                    <strong>Only Excel files (.xlsx, .xls) are supported.</strong>
                  </div>
                </Callout>
                <Flex mb={3} flexDirection="row" alignItems="stretch" style={{ width: 360 }}>
                  <FileInput
                    text={selectedFile ? selectedFile.name : "Choose Excel file..."}
                    inputProps={{ accept: ".xlsx,.xls", ref: fileInputRef, onChange: handleFileChange }}
                    style={{ marginRight: 8, width: 260 }}
                  />
                  <Button
                    icon={IconNames.UPLOAD}
                    text="Upload"
                    onClick={async () => {
                      await handleUpload();
                    }}
                    disabled={!selectedFile || uploading}
                    intent={Intent.PRIMARY}
                    loading={uploading}
                  />
                </Flex>
                {uploading && (
                  <div style={{ width: 360, marginBottom: 8 }}>
                    <ProgressBar value={uploadProgress} animate intent={Intent.PRIMARY} />
                  </div>
                )}
                {uploadStatus && activeTab === "upload" && (
                  <Callout intent={uploadStatus.includes("success") ? Intent.SUCCESS : Intent.DANGER}>
                    {uploadStatus}
                  </Callout>
                )}
              </div>
            }
          />
        </Tabs>
      </div>
    </Dialog>
  );
};

export default AgenticAIModalDialog;
